import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AdditionalRateType } from '../../models/additional-rate-type.model';
import { initialAdditionalRatesSlice } from './additional-rates.slice';
import { AdditionalRate } from '../../models/additional-rate.model';
import { MarginCoverageType } from 'src/app/recruiting/locums-margin-calculator/models/margin-coverage-type.model';
import { AdditionalRateTypes } from '../../enums/additional-rate-types.enum';

export const AdditionalRatesStore = signalStore(
    { providedIn: 'root' },

    withState(initialAdditionalRatesSlice),

    withComputed((store) => ({
        snapshot: computed(() => ({
            additionalRates: store.additionalRates()
        })),

        totalRevenue: computed(() => {
            const additionalRates = store.additionalRates();
            return additionalRates
                .filter((rate) => !rate.isDeleted)
                .reduce((total, rate) => {
                    return total + calculateAmount(rate, 'billRate');
                }, 0);
        }),

        revenue: computed(() => (selectedRate: AdditionalRate | undefined) => {
            return selectedRate ? calculateAmount(selectedRate, 'billRate') : 0;
        }),

        expense: computed(() => (selectedRate: AdditionalRate | undefined) => {
            return selectedRate ? calculateAmount(selectedRate, 'payRate') : 0;
        })
    })),

    withMethods((store) => ({
        updateAdditionalRateTypes: (additionalRateTypes: AdditionalRateType[]) => {
            patchState(store, { additionalRateTypes: additionalRateTypes });
        },
        updateMarginCoverageTypes: (marginCoverageTypes: MarginCoverageType[]) => {
            patchState(store, { marginCoverageTypes });
        },
        addAdditionalRate: (additionalRate: AdditionalRate) => {
            const additionalRates = [...store.additionalRates(), additionalRate];
            patchState(store, { additionalRates });
        },
        updateAdditionalRateFromData: (additionalRate: AdditionalRate[]) => {
            patchState(store, { additionalRates: additionalRate });
        },
        updateAdditionalRateField: (additionalRate: Partial<AdditionalRate>) => {
            const updatedAdditionalRatesList = store.additionalRates().map((rate) => {
                if (rate.additionalRateTypeId === additionalRate.additionalRateTypeId) {
                    return { ...rate, ...additionalRate };
                }
                return rate;
            });

            patchState(store, { additionalRates: updatedAdditionalRatesList });
        },
        removeAdditionalRate: (additionalRateTypeId: AdditionalRateTypes) => {
            const additionalRates = store
                .additionalRates()
                .filter((rate) => rate.additionalRateTypeId !== additionalRateTypeId);
            patchState(store, { additionalRates });
        },
        getState: () => store,
        getSnapshot: () => store.snapshot()
    })),
    environment.storeWithDevTools('locums-margin-calculator-additional-rates-store')
);

/**
 * Calculates the amount based on the provided additional rate and rate type.
 *
 * The calculation logic varies depending on the `additionalRateTypeId`:
 * - For `ON_CALL_24HOURS`, `WEEKEND_CALL`, and `NIGHT_SHIFT_CALL`, the amount is calculated as `rate * expectedShifts`.
 * - For `CALL_BACK`, the amount is calculated as `rate * expectedHours`.
 * - For other types, the amount defaults to `0`.
 *
 * @param {AdditionalRate} additionalRate - The additional rate object containing rate details and type information.
 * @param {'billRate' | 'payRate'} rateType - The type of rate to use for the calculation (`billRate` or `payRate`).
 * @returns {number} The calculated amount based on the rate type and additional rate type. Returns `0` if no calculation applies.
 */
function calculateAmount(additionalRate: AdditionalRate, rateType: 'billRate' | 'payRate'): number {
    const rate = additionalRate[rateType] ?? 0;
    const shiftsBasedTypes = [
        AdditionalRateTypes.ON_CALL_24HOURS,
        AdditionalRateTypes.WEEKEND_CALL,
        AdditionalRateTypes.NIGHT_SHIFT_CALL
    ];

    if (shiftsBasedTypes.includes(additionalRate.additionalRateTypeId)) {
        const expectedShifts = additionalRate.expectedShifts ?? 0;
        return rate * expectedShifts;
    }

    const hoursBasedTypes = [AdditionalRateTypes.CALL_BACK];

    if (hoursBasedTypes.includes(additionalRate.additionalRateTypeId)) {
        const expectedHours = additionalRate.expectedHours ?? 0;
        return rate * expectedHours;
    }

    return 0;
}
